<template>
  <v-dialog v-model="isOpen" max-width="540">
    <v-card class="px-3 py-2">
      <v-card-title class="mx-3 mt-2">Exportar CSV</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <v-autocomplete
              v-model="selectedHeaders"
              :items="headers"
              item-title="title"
              item-value="value"
              label="Columnas a exportar"
              multiple
              chips
              clearable
              :return-object="false"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-radio-group v-model="exportScope" row>
              <v-radio label="Lo que veo (paginado + filtros)" value="paged" />
              <v-radio label="Todos los datos filtrados" value="filtered" />
              <v-radio label="Todo (ignorar filtros/paginación)" value="all" />
            </v-radio-group>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="closeDialog">Cancelar</v-btn>
        <v-btn color="primary" @click="exportCSV" :loading="loading">Exportar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useExportDialogStore } from '@/store/exportDialog';
import { configRegistry } from '@/lib/configRegistry';
import { usePreferencesStore } from '@/store/preferences';
import { useLayoutStore } from '@/store/layout';

const exportDialogStore = useExportDialogStore();
const preferencesStore = usePreferencesStore();
const layoutStore = useLayoutStore();

const isOpen = computed(() => exportDialogStore.isOpen);
const moduleName = computed(() => exportDialogStore.moduleName);
const fileName = computed(() => exportDialogStore.fileName);
const exportCsvFn = computed(() => exportDialogStore.exportCsv);
const loading = ref(false);

const headers = ref<any[]>([]);
const selectedHeaders = ref<string[]>([]);
const exportScope = ref<'paged'|'filtered'|'all'>('paged');

const layoutParams = computed(() => {
  if (!moduleName.value) return {};
  const layout = layoutStore.getLayout(moduleName.value);
  if (!layout) return {};
  return {
    page: layout.page,
    itemsPerPage: layout.itemsPerPage,
    sort: layout.sortBy ? (layout.direction === 'desc' ? `-${layout.sortBy}` : layout.sortBy) : '',
    filters: layout.filters || {},
    totalItems: layout.totalItems
  };
});

watch(moduleName, async (modName) => {
  if (!modName) return;
  const loader = configRegistry[modName];
  if (!loader) return;
  const mod = await loader();
  headers.value = mod.HEADERS.filter((h:any) => !!h.title);

  const userPrefs = preferencesStore.preferences?.[modName]?.headers?.data || [];
  selectedHeaders.value = userPrefs.length
    ? userPrefs.map((h: any) => typeof h === 'string' ? h : h.value)
    : headers.value.map((h: any) => h.value);
}, { immediate: true });

function closeDialog() {
  exportDialogStore.closeDialog();
}

const exportColumns = computed(() =>
  headers.value.filter(h => selectedHeaders.value.includes(h.value))
    .map(h => ({ title: h.title, value: h.value }))
);

function flatFilters(filters: Record<string, any> = {}) {
  if (!filters) return {};
  return Object.entries(filters)
    .filter(([_, v]) => v != null && v !== '')
    .reduce((acc, [k, v]) => {
      acc[`filter[${k}]`] = v;
      return acc;
    }, {} as Record<string, any>);
}

async function exportCSV() {
  if (!exportCsvFn.value) {
    alert('No hay exportador configurado');
    return;
  }
  loading.value = true;

  const columns = exportColumns.value;
  const layout = layoutParams.value;
  let query: Record<string, any> = {};
  if (exportScope.value === 'paged') {
    query = {
      page: layout.page,
      itemsPerPage: layout.itemsPerPage,
      sort: layout.sort,
      ...flatFilters(layout.filters),
    };
  } else if (exportScope.value === 'filtered') {
    query = {
      page: 1,
      itemsPerPage: layout.totalItems,
      sort: layout.sort,
      ...flatFilters(layout.filters),
    };
  } else if (exportScope.value === 'all') {
    query = {
      page: 1,
      itemsPerPage: layout.totalItems,
      sort: layout.sort
    };
  }
  const searchParams = new URLSearchParams(query).toString();

  try {
    const response = await exportCsvFn.value(searchParams, { columns });

    // Asegúrate de esto:
    let blob;
    if (response.data instanceof Blob) {
      blob = response.data;
    } else {
      // Si llega como string, lo convertimos a Uint8Array (más seguro que pasar a Blob directo)
      const byteCharacters = atob(response.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      blob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
    }

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName.value + '.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    closeDialog();
  } catch (e) {
    console.error(e);
  }finally{
    loading.value = false;
  }
}
</script>
